import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, CircleHelp, ImagePlus } from "lucide-react";
import { CATALOG_URL, SITE_URL } from "../../config/site";
import SEO from "../../components/SEO";
import FramePhotoEditModal from "../../components/FramePhotoEditModal";
import FrameHowItWorksModal from "../../components/FrameHowItWorksModal";
import FrameRealGallery from "../../components/FrameRealGallery";
import FrameModelLinks from "../../components/FrameModelLinks";
import FrameProductVideo from "../../components/FrameProductVideo";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";
import { useCart } from "../../cart/CartContext";
import { getFrameProductV2 } from "../../catalog/frameProductsV2";
import {
  MARCO_MOSAICO_COLORS,
  MARCO_MOSAICO_CONFIG,
} from "../../constants/marcoMosaico";
import "./MarcoMosaico.css";

const createEmptySlot = () => ({
  image: null,
  fileName: "",
  position: { x: 0, y: 0 },
  scale: 1,
});

const createInitialSlots = () => {
  return MARCO_MOSAICO_CONFIG.photoBoxes.reduce((slots, box) => {
    slots[box.id] = createEmptySlot();
    return slots;
  }, {});
};

const MARCO_MOSAICO_VIEWPORT = {
  x: 0,
  y: 0,
  w: MARCO_MOSAICO_CONFIG.canvas.w,
  h: MARCO_MOSAICO_CONFIG.canvas.h,
};

const toViewportBox = (box) => ({
  ...box,
  x: box.x - MARCO_MOSAICO_VIEWPORT.x,
  y: box.y - MARCO_MOSAICO_VIEWPORT.y,
});

const MARCO_MOSAICO_DISPLAY_BOXES = MARCO_MOSAICO_CONFIG.photoBoxes.map(toViewportBox);
const MARCO_MOSAICO_MOBILE_DISPLAY_BOXES =
  MARCO_MOSAICO_CONFIG.mobilePhotoBoxes.map(toViewportBox);
const MARCO_MOSAICO_IMAGE_BOXES = MARCO_MOSAICO_CONFIG.imageBoxes.map(toViewportBox);
const clampScale = (value) => Math.min(Math.max(value, 1), 4);
const MARCO_MOSAICO_REAL_GALLERY = [
  {
    src: "/images/frame-gallery/frame-gallery-mosaico/frame-gallery-mosaico-nevera.webp",
    alt: "Marco Mosaico con cuatro fotografías colocado en una nevera",
  },
  {
    src: "/images/frame-gallery/frame-gallery-mosaico/frame-gallery-mosaico-estanteria.webp",
    alt: "Marco Mosaico con soporte decorando una estantería",
  },
  {
    src: "/images/frame-gallery/frame-gallery-mosaico/frame-gallery-mosaico-mesa.webp",
    alt: "Primer plano del Marco Mosaico con cuatro fotografías",
  },
];

export default function MarcoMosaico() {
  const { addItem } = useCart();
  const canvasRef = useRef(null);
  const isDraggingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const pinchDistanceRef = useRef(null);
  const pinchStartScaleRef = useRef(1);
  const touchTapRef = useRef(null);
  const mouseTapRef = useRef(null);
  const photoHintTimerRef = useRef(null);

  const [selectedColor, setSelectedColor] = useState(MARCO_MOSAICO_COLORS[1]);
  const [quantity, setQuantity] = useState(1);
  const [cartMessage, setCartMessage] = useState("");
  const [frameImage, setFrameImage] = useState(null);
  const [activeSlotId, setActiveSlotId] = useState("topLeft");
  const [editingSlotId, setEditingSlotId] = useState(null);
  const [slots, setSlots] = useState(createInitialSlots);
  const [catalogProduct, setCatalogProduct] = useState(null);
  const [isMobileOverlay, setIsMobileOverlay] = useState(false);
  const [photoHintMessage, setPhotoHintMessage] = useState("");
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const hasUploadedPhotos = Object.values(slots).some((slot) => Boolean(slot.image));
  const editingBox = MARCO_MOSAICO_IMAGE_BOXES.find(
    (box) => box.id === editingSlotId,
  );
  const product =
    catalogProduct || getFrameProductV2(MARCO_MOSAICO_CONFIG.id);
  const totalPrice = (product?.price || 0) * quantity;
  const editingLabel = MARCO_MOSAICO_CONFIG.photoBoxes.find((box) => box.id === editingSlotId)?.shortLabel || "foto";
  const overlayBoxes = isMobileOverlay
    ? MARCO_MOSAICO_MOBILE_DISPLAY_BOXES
    : MARCO_MOSAICO_DISPLAY_BOXES;

  useEffect(() => {
    return () => {
      window.clearTimeout(photoHintTimerRef.current);
    };
  }, []);

  const clampPosition = (x, y, drawWidth, drawHeight, box) => {
    const limitX = Math.max(0, (drawWidth - box.w) / 2);
    const limitY = Math.max(0, (drawHeight - box.h) / 2);

    return {
      x: Math.min(Math.max(x, -limitX), limitX),
      y: Math.min(Math.max(y, -limitY), limitY),
    };
  };

  const getCanvasPoint = useCallback((event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = event.touches?.[0]?.clientX ?? event.clientX;
    const clientY = event.touches?.[0]?.clientY ?? event.clientY;

    return {
      x: ((clientX - rect.left) / rect.width) * canvas.width,
      y: ((clientY - rect.top) / rect.height) * canvas.height,
      clientX,
      clientY,
    };
  }, []);

  const getSlotAtPoint = useCallback((point) => {
    return MARCO_MOSAICO_IMAGE_BOXES.find((box) => (
      point.x >= box.x &&
      point.x <= box.x + box.w &&
      point.y >= box.y &&
      point.y <= box.y + box.h
    ));
  }, []);

  const updateSlot = useCallback((slotId, updater) => {
    setSlots((current) => ({
      ...current,
      [slotId]: updater(current[slotId]),
    }));
  }, []);

  const resetFramePhotos = () => {
    setSlots(createInitialSlots());
    setActiveSlotId("topLeft");
    setEditingSlotId(null);
    setPhotoHintMessage("");
    isDraggingRef.current = false;
    pinchDistanceRef.current = null;
    touchTapRef.current = null;
    window.clearTimeout(photoHintTimerRef.current);
  };

  const loadSlotFile = (slotId, file) => {
    if (!file) return;

    const image = new Image();

    image.onload = () => {
      updateSlot(slotId, () => ({
        image,
        fileName: file.name,
        position: { x: 0, y: 0 },
        scale: 1,
      }));

      setActiveSlotId(slotId);
      setPhotoHintMessage("Foto añadida. Tócala para ajustarla.");
      window.clearTimeout(photoHintTimerRef.current);
      photoHintTimerRef.current = window.setTimeout(() => {
        setPhotoHintMessage("");
      }, 3000);
    };

    image.src = URL.createObjectURL(file);
  };

  const handleUpload = (slotId, event) => {
    loadSlotFile(slotId, event.target.files?.[0]);
    event.target.value = "";
  };

  const openEditor = (slotId) => {
    if (!slots[slotId]?.image) return;

    setActiveSlotId(slotId);
    setEditingSlotId(slotId);
  };

  const confirmEditor = ({ position, scale }) => {
    updateSlot(editingSlotId, (slot) => ({
      ...slot,
      position,
      scale,
    }));
    setEditingSlotId(null);
  };

  const changeSlotScale = useCallback((slotId, value) => {
    updateSlot(slotId, (slot) => ({
      ...slot,
      scale: clampScale(Number(value)),
    }));
  }, [updateSlot]);

  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;

    return Math.sqrt(dx * dx + dy * dy);
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 920px)");
    const updateOverlayMode = () => setIsMobileOverlay(mediaQuery.matches);

    updateOverlayMode();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateOverlayMode);
    } else {
      mediaQuery.addListener(updateOverlayMode);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", updateOverlayMode);
      } else {
        mediaQuery.removeListener(updateOverlayMode);
      }
    };
  }, []);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const response = await fetch(CATALOG_URL);
        const data = await response.json();
        const foundProduct = data.products?.find(
          (item) => item.id === MARCO_MOSAICO_CONFIG.id,
        );

        if (foundProduct) {
          setCatalogProduct(foundProduct);
          setQuantity(foundProduct.quantities?.[0] || 1);
        }
      } catch (error) {
        console.error("Error loading Marco Mosaico product", error);
      }
    };

    loadProduct();
  }, []);

  useEffect(() => {
    const image = new Image();

    image.onload = () => {
      setFrameImage(image);
    };

    image.src = selectedColor.frameSrc;
  }, [selectedColor.frameSrc]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !frameImage) return;

    const ctx = canvas.getContext("2d");

    canvas.width = MARCO_MOSAICO_VIEWPORT.w;
    canvas.height = MARCO_MOSAICO_VIEWPORT.h;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(
      frameImage,
      0,
      0,
      canvas.width,
      canvas.height,
    );

    MARCO_MOSAICO_IMAGE_BOXES.forEach((box) => {
      const slot = slots[box.id];
      if (!slot?.image) return;

      const imgRatio = slot.image.width / slot.image.height;
      const boxRatio = box.w / box.h;

      let drawWidth;
      let drawHeight;

      if (imgRatio > boxRatio) {
        drawHeight = box.h;
        drawWidth = drawHeight * imgRatio;
      } else {
        drawWidth = box.w;
        drawHeight = drawWidth / imgRatio;
      }

      drawWidth *= slot.scale;
      drawHeight *= slot.scale;

      const offsetX = (box.w - drawWidth) / 2;
      const offsetY = (box.h - drawHeight) / 2;
      const limitedPosition = clampPosition(
        slot.position.x,
        slot.position.y,
        drawWidth,
        drawHeight,
        box,
      );

      ctx.save();
      ctx.beginPath();
      ctx.rect(box.x, box.y, box.w, box.h);
      ctx.clip();
      ctx.drawImage(
        slot.image,
        box.x + offsetX + limitedPosition.x,
        box.y + offsetY + limitedPosition.y,
        drawWidth,
        drawHeight,
      );
      ctx.restore();
    });
  }, [frameImage, slots]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const wheelHandler = (event) => {
      event.preventDefault();
      event.stopPropagation();

      const point = getCanvasPoint(event);
      const slot = getSlotAtPoint(point);
      const slotId = slot?.id || activeSlotId;

      if (!slots[slotId]?.image) return;

      setActiveSlotId(slotId);

      const delta = event.deltaY > 0 ? -0.12 : 0.12;
      changeSlotScale(slotId, slots[slotId].scale + delta);
    };

    canvas.addEventListener("wheel", wheelHandler, {
      passive: false,
    });

    return () => {
      canvas.removeEventListener("wheel", wheelHandler);
    };
  }, [activeSlotId, changeSlotScale, getCanvasPoint, getSlotAtPoint, slots]);

  const handlePointerStart = (event) => {
    if (event.cancelable) {
      event.preventDefault();
    }

    const point = getCanvasPoint(event);
    const slot = getSlotAtPoint(point);

    if (slot) {
      setActiveSlotId(slot.id);
    }

    if (event.type.startsWith("mouse")) {
      mouseTapRef.current = {
        slotId: slot?.id || null,
        startX: point.clientX,
        startY: point.clientY,
        moved: false,
      };
    }
    if (event.touches?.length === 2 && slot && slots[slot.id]?.image) {
      isDraggingRef.current = false;
      pinchDistanceRef.current = getTouchDistance(event.touches);
      pinchStartScaleRef.current = slots[slot.id].scale;
      return;
    }

    if (!slot || !slots[slot.id]?.image) return;

    isDraggingRef.current = true;
    lastPointRef.current = {
      x: point.clientX,
      y: point.clientY,
    };
  };

  const handleTouchStart = (event) => {
    const point = getCanvasPoint(event);
    const slot = getSlotAtPoint(point);

    touchTapRef.current = {
      slotId: slot?.id || null,
      startX: point.clientX,
      startY: point.clientY,
      moved: false,
    };

    if (slot) {
      setActiveSlotId(slot.id);
    }
  };

  const handleTouchMove = (event) => {
    if (!touchTapRef.current) return;

    const point = getCanvasPoint(event);
    const dx = point.clientX - touchTapRef.current.startX;
    const dy = point.clientY - touchTapRef.current.startY;

    if (Math.sqrt(dx * dx + dy * dy) > 12) {
      touchTapRef.current.moved = true;
    }
  };

  const handleTouchEnd = () => {
    const tap = touchTapRef.current;
    touchTapRef.current = null;
    stopDragging();

    if (!tap || tap.moved || !tap.slotId || !slots[tap.slotId]?.image) return;

    setEditingSlotId(tap.slotId);
  };

  const handlePointerMove = (event) => {
    if (event.cancelable) {
      event.preventDefault();
    }

    if (event.type.startsWith("mouse") && mouseTapRef.current) {
      const point = getCanvasPoint(event);
      const dx = point.clientX - mouseTapRef.current.startX;
      const dy = point.clientY - mouseTapRef.current.startY;

      if (Math.sqrt(dx * dx + dy * dy) > 12) {
        mouseTapRef.current.moved = true;
      }
    }

    if (event.touches?.length === 2 && pinchDistanceRef.current) {
      const currentDistance = getTouchDistance(event.touches);
      const zoomFactor = currentDistance / pinchDistanceRef.current;
      const nextScale = pinchStartScaleRef.current * zoomFactor;

      changeSlotScale(activeSlotId, nextScale);
      return;
    }

    if (!isDraggingRef.current) return;

    const point = getCanvasPoint(event);
    const dx = point.clientX - lastPointRef.current.x;
    const dy = point.clientY - lastPointRef.current.y;

    updateSlot(activeSlotId, (slot) => ({
      ...slot,
      position: {
        x: slot.position.x + dx,
        y: slot.position.y + dy,
      },
    }));

    lastPointRef.current = {
      x: point.clientX,
      y: point.clientY,
    };
  };

  const stopDragging = () => {
    isDraggingRef.current = false;
    pinchDistanceRef.current = null;
  };
  const handlePointerEnd = () => {
    const tap = mouseTapRef.current;
    mouseTapRef.current = null;
    stopDragging();

    if (!tap || tap.moved || !tap.slotId || !slots[tap.slotId]?.image) return;

    openEditor(tap.slotId);
  };

  const handleAddToCart = () => {
    addItem({
      productId: product.id,
      productName: product.name,
      category: product.category,
      type: product.type,
      quantity,
      unitPrice: product.price || 0,
      currency: product.currency || "EUR",
      options: {
        color: {
          label: "Color",
          value: selectedColor.id,
          valueLabel: selectedColor.name,
        },
      },
      assets: [],
      notes: [
        "Las fotos subidas en el editor son solo una vista previa y no forman parte del pedido.",
      ],
    });

    setCartMessage("Marco anadido al carrito.");
  };

  return (
    <>
      <SEO
        title="Marco Mosaico para cuatro imanes personalizados | Imalandia"
        description="Prueba cuatro fotos en un marco Mosaico para imanes de 5x5 cm y elige el color que mejor encaja con tus recuerdos."
        canonical={`${SITE_URL}/marco-mosaico`}
      />

      <Header />

      <main className="marco-mosaico-page">
        <section className="marco-mosaico-layout">
          <div className="marco-mosaico-heading">
            <p>Marco para cuatro imanes 5x5 cm</p>
            <h1>Marco Mosaico</h1>
          </div>

          <div className="marco-mosaico-note">
            <span>
              Las fotos son solo una vista previa. El pedido del marco no
              incluye imanes personalizados.
            </span>
            <button
              type="button"
              className="frame-help-trigger"
              onClick={() => setHelpModalOpen(true)}
            >
              <CircleHelp aria-hidden="true" />
              Como funciona
            </button>
          </div>

          <aside className="marco-mosaico-side marco-mosaico-side--colors">
            <div className="marco-mosaico-card">
              <h2>Elige color</h2>

              <div className="marco-mosaico-colors">
                {MARCO_MOSAICO_COLORS.map((color) => (
                  <button
                    key={color.id}
                    type="button"
                    className={
                      selectedColor.id === color.id
                        ? "marco-mosaico-color active"
                        : "marco-mosaico-color"
                    }
                    style={{ background: color.swatch }}
                    title={color.name}
                    aria-label={color.name}
                    onClick={() => setSelectedColor(color)}
                  />
                ))}
              </div>
            </div>
          </aside>

          <div className="marco-mosaico-preview">
            <div className="marco-mosaico-canvas-wrap">
              <canvas
                ref={canvasRef}
                width={MARCO_MOSAICO_VIEWPORT.w}
                height={MARCO_MOSAICO_VIEWPORT.h}
                role="img"
                aria-label="Vista previa del Marco Mosaico con cuatro fotos"
                onMouseDown={handlePointerStart}
                onMouseMove={handlePointerMove}
                onMouseUp={handlePointerEnd}
                onMouseLeave={stopDragging}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
              />

              {overlayBoxes.map((box) => {
                const hasImage = Boolean(slots[box.id]?.image);
                const cameraButtonSize = isMobileOverlay ? 51 : 34;
                const overlayBox = hasImage
                  ? {
                      x: box.x + box.w - cameraButtonSize - 4,
                      y: box.y + 8,
                      w: cameraButtonSize,
                      h: cameraButtonSize,
                    }
                  : box;

                return (
                  <label
                    key={box.id}
                    className={
                      hasImage
                        ? "marco-mosaico-drop-zone has-image"
                        : "marco-mosaico-drop-zone"
                    }
                    style={{
                      left: `${(overlayBox.x / MARCO_MOSAICO_VIEWPORT.w) * 100}%`,
                      top: `${(overlayBox.y / MARCO_MOSAICO_VIEWPORT.h) * 100}%`,
                      width: `${(overlayBox.w / MARCO_MOSAICO_VIEWPORT.w) * 100}%`,
                      height: `${(overlayBox.h / MARCO_MOSAICO_VIEWPORT.h) * 100}%`,
                    }}
                    onClick={(event) => {
                      if (hasImage) {
                        event.preventDefault();
                        openEditor(box.id);
                        return;
                      }

                      setActiveSlotId(box.id);
                    }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) => handleUpload(box.id, event)}
                    />
                    <span>
                      {hasImage ? (
                        <Camera size={24} strokeWidth={2.5} />
                      ) : (
                        <>
                          <ImagePlus size={22} strokeWidth={2.4} />
                          <strong>Clic aqui</strong>
                          <small>Subir foto</small>
                        </>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
            {isMobileOverlay && hasUploadedPhotos && (
              <p className="marco-mosaico-photo-hint" aria-live="polite">
                {photoHintMessage || "Toca una foto para ajustar el encuadre."}
              </p>
            )}
          </div>

          <button
            type="button"
            className="marco-mosaico-reset-all"
            onClick={resetFramePhotos}
            disabled={!hasUploadedPhotos}
          >
            Borrar fotos
          </button>

          <div className="marco-mosaico-bottom">
            <div className="marco-mosaico-card marco-mosaico-order">
              <h2>Pedido</h2>

              <div className="marco-mosaico-order-row">
                <label className="marco-mosaico-quantity">
                  Cantidad
                  <select
                    value={quantity}
                    onChange={(event) => setQuantity(Number(event.target.value))}
                  >
                    {product.quantities.map((qty) => (
                      <option key={qty} value={qty}>
                        {qty} unidad{qty > 1 ? "es" : ""}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="marco-mosaico-price">
                  <span>{(product.price || 0).toFixed(2)} EUR / unidad</span>
                  <strong>Total: {totalPrice.toFixed(2)} EUR</strong>
                </div>
              </div>

              {cartMessage && (
                <p className="marco-mosaico-cart-message">{cartMessage}</p>
              )}

              <button
                type="button"
                className="marco-mosaico-cart-button"
                onClick={handleAddToCart}
              >
                Anadir al carrito
              </button>

              <Link to="/carrito" className="marco-mosaico-cart-link">
                Ver carrito
              </Link>
            </div>
          </div>

          <div className="marco-mosaico-real-gallery">
            <FrameRealGallery
              images={MARCO_MOSAICO_REAL_GALLERY}
              title="Cuatro momentos, una sola historia"
              description="Reúne tus fotos favoritas y conviértelas en un pequeño rincón lleno de recuerdos."
            />
          </div>
          <FrameProductVideo
            videoSrc="/videos/marco-mosaico.mp4"
            title="Marco Mosaico en accion"
            description="Cuatro recuerdos en una sola pieza, con presencia y mucho juego visual."
            label="Video del Marco Mosaico personalizado"
          />
          <FrameModelLinks currentModel="mosaico" />
        </section>
      </main>

      {editingSlotId && editingBox && (
        <FramePhotoEditModal
          box={editingBox}
          slot={slots[editingSlotId]}
          title={`Ajustar ${editingLabel.toLowerCase()}`}
          onClose={() => setEditingSlotId(null)}
          onConfirm={confirmEditor}
          onChangeImage={(file) => loadSlotFile(editingSlotId, file)}
        />
      )}

      {helpModalOpen && (
        <FrameHowItWorksModal onClose={() => setHelpModalOpen(false)} />
      )}

      <Footer />
    </>
  );
}




